'use client';

import { useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import type { WebGLRenderer } from 'three';

/**
 * drei's `useGLTF` already enables Draco + meshopt by default.
 * This module adds KTX2/Basis transcoder support for compressed textures.
 */

let ktx2: KTX2Loader | null = null;
let ktx2Ready = false;

function ensureKtx2(renderer: WebGLRenderer): KTX2Loader {
  if (!ktx2) {
    ktx2 = new KTX2Loader();
    ktx2.setTranscoderPath('https://cdn.jsdelivr.net/npm/three@0.172.0/examples/jsm/libs/basis/');
    ktx2.detectSupport(renderer);
    ktx2Ready = true;
  }
  return ktx2;
}

/** Pass as the 4th arg to `useGLTF(url, true, true, extendGltfLoader)`. */
export function extendGltfLoader(loader: { setKTX2Loader?: (k: KTX2Loader) => void }): void {
  if (typeof window === 'undefined') return;
  if (ktx2 && typeof loader.setKTX2Loader === 'function') {
    loader.setKTX2Loader(ktx2);
  }
}

/** Mount inside Canvas once to bind KTX2 to the WebGL renderer. */
export function ConfigureGltfLoaders() {
  const gl = useThree((s) => s.gl);
  useEffect(() => {
    ensureKtx2(gl);
  }, [gl]);
  return null;
}

export function configureGltfLoaders(renderer?: WebGLRenderer | null): void {
  if (renderer) ensureKtx2(renderer);
}

export function isGltfLoaderSetupReady(): boolean {
  return ktx2Ready;
}
